import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, User, CheckCircle, ArrowLeft } from 'lucide-react';

const TimeSlotSelection = () => {
  const { requestId } = useParams();
  const [searchParams] = useSearchParams();
  const [availableDates, setAvailableDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [customerInfo, setCustomerInfo] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Generate default available dates (next 7 days, excluding weekends)
  const generateDefaultDates = () => {
    const dates = [];
    const today = new Date();
    let currentDate = new Date(today);
    currentDate.setDate(currentDate.getDate() + 1); // Start from tomorrow

    while (dates.length < 7) {
      // Skip weekends (Saturday = 6, Sunday = 0)
      if (currentDate.getDay() !== 0 && currentDate.getDay() !== 6) {
        dates.push(new Date(currentDate));
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    setAvailableDates(dates);
  };

  // Fetch available dates from the backend
  const fetchAvailableDates = useCallback(async () => {
    try {
      setLoading(true);
      console.log('🔄 Fetching available dates for request ID:', requestId);
      
      const response = await fetch(`/api/admin/pending-inspection/${requestId}`);
      
      console.log('📡 API Response status:', response.status);
      
      if (response.ok) {
        const pendingInspection = await response.json();
        console.log('✅ Received pending inspection data:', pendingInspection);
        
        // Get available dates from the pending inspection record
        if (pendingInspection.availableDates && pendingInspection.availableDates.length > 0) {
          const dates = pendingInspection.availableDates.map(dateStr => new Date(dateStr));
          console.log('📅 Setting available dates:', dates);
          setAvailableDates(dates);
        } else {
          console.warn('No available dates found in pending inspection record');
          // Fallback to default dates if none found
          generateDefaultDates();
        }
      } else {
        const errorData = await response.text();
        console.error('Failed to fetch pending inspection:', response.status, errorData);
        // Fallback to default dates
        generateDefaultDates();
      }
    } catch (error) {
      console.error('Error fetching available dates:', error);
      // Fallback to default dates
      generateDefaultDates();
    } finally {
      setLoading(false);
    }
  }, [requestId]);

  // Get customer info from URL parameters and fetch available dates
  useEffect(() => {
    const customerName = searchParams.get('name');
    const customerEmail = searchParams.get('email');
    const propertyAddress = searchParams.get('property');
    const district = searchParams.get('district');

    setCustomerInfo({
      name: customerName,
      email: customerEmail,
      address: propertyAddress,
      district: district,
      phone: '+65 0000 0000' // Default phone number
    });

    // Fetch available dates from the backend for this specific request
    fetchAvailableDates();
  }, [searchParams, requestId, fetchAvailableDates]);

  // Generate 30-minute time slots for a given date
  const generateTimeSlots = (date) => {
    const slots = [];
    
    // Morning slots: 9:00 AM - 12:00 PM (every 30 minutes)
    for (let hour = 9; hour < 12; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const startTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const endHour = minute === 30 ? hour + 1 : hour;
        const endMinute = minute === 30 ? 0 : 30;
        const endTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
        
        slots.push({
          id: `morning-${hour}-${minute}`,
          period: 'Morning',
          time: `${startTime} - ${endTime}`,
          startTime: startTime,
          endTime: endTime,
          available: true
        });
      }
    }

    // Afternoon slots: 1:00 PM - 5:00 PM (every 30 minutes)
    for (let hour = 13; hour < 17; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const startTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        const endHour = minute === 30 ? hour + 1 : hour;
        const endMinute = minute === 30 ? 0 : 30;
        const endTime = `${endHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')}`;
        
        // Convert to 12-hour format for display
        const displayStartHour = hour > 12 ? hour - 12 : hour;
        const displayEndHour = endHour > 12 ? endHour - 12 : endHour;
        const displayTime = `${displayStartHour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} - ${displayEndHour.toString().padStart(2, '0')}:${endMinute.toString().padStart(2, '0')} PM`;
        
        slots.push({
          id: `afternoon-${hour}-${minute}`,
          period: 'Afternoon',
          time: displayTime,
          startTime: startTime,
          endTime: endTime,
          available: true
        });
      }
    }

    return slots;
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTimeSlot(null);
  };

  const handleTimeSlotSelect = (timeSlot) => {
    setSelectedTimeSlot(timeSlot);
  };

  const handleSubmit = async () => {
    if (!selectedDate || !selectedTimeSlot) {
      alert('Please select both a date and time slot');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`/api/admin/confirm-inspection-slot`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          selectedDate: selectedDate.toISOString(),
          selectedTime: selectedTimeSlot.startTime || selectedTimeSlot,
          customerInfo: customerInfo
        })
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        // Get the actual error message from the server
        const errorData = await response.text();
        let errorMessage = 'Failed to confirm appointment';
        
        try {
          const jsonError = JSON.parse(errorData);
          errorMessage = jsonError.message || jsonError.error || errorMessage;
        } catch (e) {
          // If it's not JSON, use the text response
          errorMessage = errorData || errorMessage;
        }
        
        console.error('Server error response:', errorData);
        throw new Error(`Server error (${response.status}): ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error confirming appointment:', error);
      alert('Failed to confirm appointment. Please try again or contact us directly.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full text-center"
        >
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Appointment Confirmed!</h2>
          <p className="text-gray-600 mb-6">
            Your inspection appointment has been scheduled for:
          </p>
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
            <p className="font-semibold text-orange-800">
              {selectedDate.toLocaleDateString('en-SG', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
            <p className="text-orange-700">{selectedTimeSlot.time}</p>
          </div>
          <p className="text-sm text-gray-500">
            Our team will contact you 24 hours before the appointment to confirm details.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto p-6">
        
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-sm p-6 mb-6"
        >
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4">
              <Calendar className="w-6 h-6 text-orange-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Schedule Your Inspection</h1>
              <p className="text-gray-600">Select your preferred date and 30-minute time slot</p>
            </div>
          </div>

          {/* Customer Information */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
              <User className="w-4 h-4 mr-2" />
              Appointment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Customer:</span>
                <span className="ml-2 font-medium">{customerInfo.name}</span>
              </div>
              <div>
                <span className="text-gray-500">Email:</span>
                <span className="ml-2 font-medium">{customerInfo.email}</span>
              </div>
              <div className="md:col-span-2">
                <span className="text-gray-500">Property:</span>
                <span className="ml-2 font-medium flex items-center">
                  <MapPin className="w-3 h-3 mr-1" />
                  {customerInfo.address} ({customerInfo.district})
                </span>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Date Selection */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-sm p-6 mb-6"
        >
          <h2 className="text-xl font-bold text-gray-900 mb-4">Step 1: Select Date</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {availableDates.map((date, index) => (
              <button
                key={index}
                onClick={() => handleDateSelect(date)}
                className={`p-4 rounded-lg border-2 transition-all ${
                  selectedDate && selectedDate.getTime() === date.getTime()
                    ? 'border-orange-500 bg-orange-50 text-orange-700'
                    : 'border-gray-200 hover:border-orange-300 hover:bg-orange-25'
                }`}
              >
                <div className="text-center">
                  <div className="font-semibold">
                    {date.toLocaleDateString('en-SG', { weekday: 'short' })}
                  </div>
                  <div className="text-lg font-bold">
                    {date.getDate()}
                  </div>
                  <div className="text-sm text-gray-600">
                    {date.toLocaleDateString('en-SG', { month: 'short', year: 'numeric' })}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </motion.div>

        {/* Time Slot Selection */}
        {selectedDate && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-lg shadow-sm p-6 mb-6"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
              <Clock className="w-5 h-5 mr-2" />
              Step 2: Select 30-Minute Time Slot
            </h2>
            <p className="text-gray-600 mb-6">
              Available slots for {selectedDate.toLocaleDateString('en-SG', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>

            <div className="space-y-6">
              {/* Morning Slots */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Morning (9:00 AM - 12:00 PM)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {generateTimeSlots(selectedDate)
                    .filter(slot => slot.period === 'Morning')
                    .map((slot) => (
                      <button
                        key={slot.id}
                        onClick={() => handleTimeSlotSelect(slot)}
                        disabled={!slot.available}
                        className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                          selectedTimeSlot && selectedTimeSlot.id === slot.id
                            ? 'border-orange-500 bg-orange-500 text-white'
                            : slot.available
                            ? 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                            : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {slot.time}
                      </button>
                    ))}
                </div>
              </div>

              {/* Afternoon Slots */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3">Afternoon (1:00 PM - 5:00 PM)</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {generateTimeSlots(selectedDate)
                    .filter(slot => slot.period === 'Afternoon')
                    .map((slot) => (
                      <button
                        key={slot.id}
                        onClick={() => handleTimeSlotSelect(slot)}
                        disabled={!slot.available}
                        className={`p-3 rounded-lg border text-sm font-medium transition-all ${
                          selectedTimeSlot && selectedTimeSlot.id === slot.id
                            ? 'border-orange-500 bg-orange-500 text-white'
                            : slot.available
                            ? 'border-gray-200 hover:border-orange-300 hover:bg-orange-50'
                            : 'border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed'
                        }`}
                      >
                        {slot.time}
                      </button>
                    ))}
                </div>
              </div>
            </div>
          </motion.div>
        )}

        {/* Confirmation */}
        {selectedDate && selectedTimeSlot && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-lg shadow-sm p-6"
          >
            <h2 className="text-xl font-bold text-gray-900 mb-4">Step 3: Confirm Appointment</h2>
            
            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-orange-800 mb-2">Selected Appointment:</h3>
              <div className="space-y-1 text-orange-700">
                <p className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  {selectedDate.toLocaleDateString('en-SG', { 
                    weekday: 'long', 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
                <p className="flex items-center">
                  <Clock className="w-4 h-4 mr-2" />
                  {selectedTimeSlot.time}
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={() => {
                  setSelectedDate(null);
                  setSelectedTimeSlot(null);
                }}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors flex items-center justify-center"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Start Over
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex-1 px-6 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-semibold"
              >
                {submitting ? 'Confirming...' : 'Confirm Appointment'}
              </button>
            </div>
          </motion.div>
        )}

      </div>
    </div>
  );
};

export default TimeSlotSelection;